import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  severity: ToastSeverity
  summary: string
  detail?: string
  /** Auto-dismiss duration in ms. 0 = sticky (must be dismissed manually). */
  life: number
}

export interface ModalEntry {
  id: string
  component: string
  props?: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let _toastCounter = 0

function makeToastId(): string {
  _toastCounter += 1
  return `toast-${Date.now()}-${_toastCounter}`
}

let _modalCounter = 0

function makeModalId(): string {
  _modalCounter += 1
  return `modal-${Date.now()}-${_modalCounter}`
}

const DEFAULT_TOAST_LIFE = 5000 // 5 seconds

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useUiStore = defineStore('ui', () => {
  // ---- Sidebar -------------------------------------------------------------
  const SIDEBAR_KEY = 'postlytics_admin_sidebar_collapsed'
  const sidebarCollapsed = ref(localStorage.getItem(SIDEBAR_KEY) === 'true')

  watch(sidebarCollapsed, (val) => {
    localStorage.setItem(SIDEBAR_KEY, String(val))
  })

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  function setSidebarCollapsed(collapsed: boolean): void {
    sidebarCollapsed.value = collapsed
  }

  // ---- Toast queue ---------------------------------------------------------
  const toasts = ref<Toast[]>([])

  function addToast(toast: Omit<Toast, 'id'>): void {
    const entry: Toast = { ...toast, id: makeToastId() }
    toasts.value.push(entry)

    if (entry.life > 0) {
      setTimeout(() => {
        removeToast(entry.id)
      }, entry.life)
    }
  }

  function removeToast(id: string): void {
    toasts.value = toasts.value.filter((t) => t.id !== id)
  }

  function clearToasts(): void {
    toasts.value = []
  }

  // Convenience helpers
  function showSuccess(summary: string, detail?: string): void {
    addToast({ severity: 'success', summary, detail, life: DEFAULT_TOAST_LIFE })
  }

  function showError(summary: string, detail?: string): void {
    addToast({ severity: 'error', summary, detail, life: 0 }) // errors are sticky
  }

  function showWarning(summary: string, detail?: string): void {
    addToast({ severity: 'warning', summary, detail, life: DEFAULT_TOAST_LIFE })
  }

  function showInfo(summary: string, detail?: string): void {
    addToast({ severity: 'info', summary, detail, life: DEFAULT_TOAST_LIFE })
  }

  // ---- Modal stack ---------------------------------------------------------
  const modals = ref<ModalEntry[]>([])

  const activeModal = computed<ModalEntry | null>(() =>
    modals.value.length > 0 ? modals.value[modals.value.length - 1] : null,
  )

  function openModal(component: string, props?: Record<string, unknown>): string {
    const id = makeModalId()
    modals.value.push({ id, component, props })
    return id
  }

  function closeModal(id?: string): void {
    if (id) {
      modals.value = modals.value.filter((m) => m.id !== id)
    } else {
      // Close top-most modal
      modals.value.pop()
    }
  }

  function clearModals(): void {
    modals.value = []
  }

  return {
    // sidebar
    sidebarCollapsed,
    toggleSidebar,
    setSidebarCollapsed,

    // toasts
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // modals
    modals,
    activeModal,
    openModal,
    closeModal,
    clearModals,
  }
})
